import { Bell } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'


import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useNotificationStore } from '@/stores/notification.store'
import type { Notification } from '@/types/api/notification'
import { cn } from '@/utils/cn'

import { useNotificationActions } from '../hooks/useNotificationActions'

import { BrowserNotificationsToggle } from './BrowserNotificationsToggle'
import { NotificationRow } from './NotificationRow'

interface NotificationMenuProps {
  triggerClassName?: string
  showLabel?: boolean
  navigateToNotifications?: boolean
}

export function NotificationMenu({
  triggerClassName,
  showLabel = true,
  navigateToNotifications = false,
}: NotificationMenuProps) {
  const [open, setOpen] = useState(false)
  
  const navigate = useNavigate()
  const location = useLocation()

  const {
    notifications,
    unreadCount,
  } = useNotificationStore()

  const {
    markAllAsRead,
    markRead,
    dismiss,
    refresh,
  } = useNotificationActions()

  const top5 = notifications.slice(0, 5)

  const isNotificationsPage =
    location.pathname === '/notifications' ||
    location.pathname.startsWith('/notifications/')

  const handleOpenChange = async (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (!nextOpen) {
      return
    }

    /*
     * Same behavior as the old header:
     * opening the notification panel marks
     * all unread notifications as read.
     */
    if (unreadCount > 0) {
      await markAllAsRead()

      try {
        await refresh()
      } catch {
        // refresh already handles the error
      }
    }
  }

  const handleNotificationClick = (
    notification: Notification
  ) => {
    if (
      notification.type ===
      'FOLLOWUPS_DUE_SUMMARY'
    ) {
      setOpen(false)
      navigate('/tasks')
      return
    }
    // Automation notifications open the record they are about
    if (notification.link) {
      setOpen(false)
      void markRead(notification)
      navigate(notification.link)
    }
  }

  const handleViewAll = () => {
    setOpen(false)
    navigate('/notifications')
  }

  const handleTriggerInteraction = (
    event: { preventDefault: () => void }
  ) => {
    if (!navigateToNotifications) {
      return
    }

    event.preventDefault()
    navigate('/notifications')
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          onPointerDown={handleTriggerInteraction}
          onClick={handleTriggerInteraction}
          className={cn(
            'relative flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
            isNotificationsPage
              ? 'bg-primary-100 text-primary-900'
              : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
            triggerClassName
          )}
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 shrink-0" />

          {showLabel && (
            <span className="overflow-hidden whitespace-nowrap">
              Notifications
            </span>
          )}

          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className={cn(
                'flex items-center justify-center rounded-full p-0 text-xs',
                showLabel
                  ? 'ml-auto h-5 min-w-5 px-1'
                  : 'absolute -right-1 -top-1 h-5 w-5'
              )}
            >
              {unreadCount > 9
                ? '9+'
                : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={8}
        className="w-[360px] p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <div>
            <p className="text-sm font-semibold">
              Notifications
            </p>

            {unreadCount > 0 && (
              <p className="text-xs text-neutral-400">
                {unreadCount} unread
              </p>
            )}
          </div>

          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-neutral-500"
              onClick={() => {
                void markAllAsRead()
              }}
            >
              Mark all read
            </Button>
          )}
        </div>

        <DropdownMenuSeparator />

        {/* Notifications */}
        {top5.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <Bell className="mx-auto mb-2 h-8 w-8 text-neutral-300" />

            <p className="text-sm text-neutral-400">
              No notifications in this workspace
            </p>
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            {top5.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                compact
                onClick={handleNotificationClick}
                onDelete={dismiss}
              />
            ))}
          </div>
        )}

        <DropdownMenuSeparator />

        {/* View all */}
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-primary-600 hover:text-primary-700"
            onClick={handleViewAll}
          >
            View all notifications
          </Button>
          <BrowserNotificationsToggle className="mt-1 px-1 text-center text-xs text-muted-foreground" />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
